import {
  type BulkUpdateAttributeDefinition,
  BulkUpdateConfirmationDefinition,
  getBulkUpdateRegistry,
  WriteStrategy,
} from '@provision/bulk-update-config';
import type { EntityName } from '@provision/entity-types';
import { arrayIfy } from '@provision/formatting-utils';
import { statesOptions } from 'src/components/AddSiteModal/use-new-site-form';
import { CustomViewEntity } from 'src/graphql/entities/custom-view/custom-view-config/custom-view-config';
import { SupportedActionQueries } from 'src/pages/Settings/rules/use-rules-engine';

const CUSTOM_VIEW_ENTITY_TO_ENTITY_NAME: Record<CustomViewEntity, EntityName> = {
  conversation: 'Conversation',
  site: 'Address',
  client: 'Client',
  project: 'Project',
};

export type BulkUpdateActionKind = 'update' | 'delete';

export interface BulkUpdateActionDefinition {
  id: string;
  label: string;
  icon: string;
  attributeKey: string;
  attributeLabel: string;
  updateAttribute: string;
  editorType: string;
  optionsQuery?: SupportedActionQueries;
  options?: { label: string; value: string; id: string }[];
  allowsClear?: boolean;
  submitMode: string;
  kind: BulkUpdateActionKind;
  accessRuleKey?: string[];
  confirmation?: BulkUpdateConfirmationDefinition;
}

type StaticOptionOverride = { label: string; value: string; id: string }[];

const STATIC_OPTIONS_OVERRIDES: Partial<Record<string, StaticOptionOverride>> = {
  'Address:state': statesOptions.map((s) => ({ id: s.value, label: s.label, value: s.value })),
};

function toActionDefinition(def: BulkUpdateAttributeDefinition): BulkUpdateActionDefinition {
  const label = def.actionLabel || def.label;

  const overrideKey = `${def.entityName}:${def.attribute}`;
  const staticOptions =
    STATIC_OPTIONS_OVERRIDES[overrideKey] ??
    (def.editor.staticOptions as StaticOptionOverride | undefined);

  const kind: BulkUpdateActionKind =
    def.execution.writeStrategy === WriteStrategy.SoftDelete ? 'delete' : 'update';

  const accessRuleKey = arrayIfy(def.validation.accessRuleKey);

  return {
    id: `${kind}-${def.attribute}`,
    label,
    icon: def.editor.icon ?? 'edit',
    attributeKey: def.attribute,
    attributeLabel: def.label,
    updateAttribute: def.attribute,
    editorType: def.editor.type,
    optionsQuery: def.editor.optionsSource as SupportedActionQueries | undefined,
    options: staticOptions,
    allowsClear: def.editor.allowsClear,
    submitMode: def.editor.submitMode ?? 'bulk-update',
    kind,
    accessRuleKey,
    confirmation: def.confirmation,
  };
}

export function getBulkUpdateActions(
  entity: CustomViewEntity,
  { isMultitenancy = false }: { isMultitenancy?: boolean } = {},
): BulkUpdateActionDefinition[] {
  const entityName = CUSTOM_VIEW_ENTITY_TO_ENTITY_NAME[entity];
  return getBulkUpdateRegistry()
    .filter((def) => def.entityName === entityName)
    .filter((def) => !isMultitenancy || def.isMultitenancyAllowed)
    .map(toActionDefinition);
}

export function mapCustomViewEntityToEntityName(entity: CustomViewEntity): EntityName {
  return CUSTOM_VIEW_ENTITY_TO_ENTITY_NAME[entity];
}
